import type { SearchLaunchablesResponse as DevplaneSearchLaunchablesResponse } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/launchable_pb";
import {
  EnvironmentStatus,
  type Environment as DevplaneEnvironment,
  type EnvironmentProvision as DevplaneEnvironmentProvision,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import type { CreateInstanceRequest as DevplaneCreateInstanceRequest } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/instance_pb";
import type {
  JsonObject,
  JsonValue,
  Timestamp as DevplaneTimestamp,
} from "@bufbuild/protobuf";
import type {
  CreateInstanceRequest,
  Environment,
  EnvironmentProvision,
  Timestamp,
} from "components/Environment/utils/types";
import { normalizeEnvironmentBytes } from "utils/instanceTypeBytes";

export interface SearchLaunchablesResponse {
  results: Environment[];
  totalResultsCount: number;
  pageResultsCount: number;
  cursor: string;
}

const legacyBrevDeployJsonOptions = {
  useProtoFieldName: true,
  enumAsInteger: true,
} as const;

function requireJsonObject(value: JsonValue, messageName: string): JsonObject {
  if (value === null || Array.isArray(value) || typeof value !== "object") {
    throw new Error(`${messageName} protobuf JSON must be an object`);
  }

  return value;
}

function mapTimestamp(timestamp: DevplaneTimestamp): Timestamp {
  return {
    seconds: Number(timestamp.seconds),
    nanos: timestamp.nanos,
  };
}

function mapCreateInstanceRequest(
  request: DevplaneCreateInstanceRequest,
): CreateInstanceRequest {
  const json = requireJsonObject(
    request.toJson(legacyBrevDeployJsonOptions),
    "CreateInstanceRequest",
  );

  return {
    ...json,
    cloud_cred_id: request.cloudCredId,
    public_key: request.publicKey,
    image_id: request.imageId,
    instance_type: request.instanceType,
    user_data_base64: request.userDataBase64,
    location: request.location,
    disk_size: request.diskSize,
    instance_id: request.instanceId,
    idempotency_key: request.idempotencyKey,
    sub_location: request.subLocation,
  };
}

function mapProvision(
  provision: DevplaneEnvironmentProvision,
): EnvironmentProvision {
  const json = {
    ...requireJsonObject(
      provision.toJson(legacyBrevDeployJsonOptions),
      "EnvironmentProvision",
    ),
  };

  if (provision.directive.case === "createInstanceRequest") {
    delete json.create_instance_request;
    return {
      ...json,
      Directive: {
        CreateInstanceRequest: mapCreateInstanceRequest(
          provision.directive.value,
        ),
      },
    };
  }

  if (provision.directive.case === "instanceId") {
    delete json.instance_id;
    return {
      ...json,
      Directive: {
        InstanceId: provision.directive.value,
      },
    };
  }

  return json;
}

function mapEnvironment(environment: DevplaneEnvironment): Environment {
  const json = normalizeEnvironmentBytes(
    requireJsonObject(
      environment.toJson(legacyBrevDeployJsonOptions),
      "Environment",
    ) as unknown as Environment,
  );

  return {
    ...json,
    environment_id: environment.environmentId,
    ...(environment.createTime && {
      create_time: mapTimestamp(environment.createTime),
    }),
    ...(environment.updateTime && {
      update_time: mapTimestamp(environment.updateTime),
    }),
    ...(environment.provision && {
      provision: mapProvision(environment.provision),
    }),
    ...(environment.status !== EnvironmentStatus.UNSPECIFIED && {
      status: environment.status,
    }),
  };
}

export function mapSearchLaunchablesResponse(
  response: DevplaneSearchLaunchablesResponse,
): SearchLaunchablesResponse {
  return {
    results: response.results.map(mapEnvironment),
    totalResultsCount: Number(response.totalResultsCount),
    pageResultsCount: Number(response.pageResultsCount),
    cursor: response.cursor,
  };
}
